/**
 * Business API returns ProductGroup rows with nested sellable variants (Product).
 */

export function formatVariantDisplayName(groupName, title) {
  const name = String(groupName ?? '').trim() || 'Товар'
  const variantTitle = String(title ?? '').trim()
  if (!variantTitle) return name
  return `${name} (${variantTitle})`
}

function parsePrice(value) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

export function normalizeProductImages(images, fallbackUrl = null) {
  const raw = Array.isArray(images) ? images : []
  const cleaned = []

  for (const item of raw) {
    if (typeof item === 'string') {
      const url = item.trim()
      if (url) cleaned.push({ url, index: cleaned.length })
      continue
    }
    if (!item || typeof item !== 'object') continue
    const url = String(item.url ?? item.imageUrl ?? item.image_url ?? '').trim()
    if (!url) continue
    const index = Number.parseInt(String(item.index), 10)
    cleaned.push({
      url,
      index: Number.isFinite(index) ? index : cleaned.length,
    })
  }

  if (cleaned.length === 0) {
    const fallback = String(fallbackUrl ?? '').trim()
    if (fallback) return [{ url: fallback, index: 0 }]
    return []
  }

  cleaned.sort((a, b) => a.index - b.index || a.url.localeCompare(b.url))
  return cleaned.map((item, index) => ({ url: item.url, index }))
}

export function primaryProductImageUrl(images, fallbackUrl = null) {
  const list = normalizeProductImages(images, fallbackUrl)
  return list[0]?.url ?? (String(fallbackUrl ?? '').trim() || null)
}

/** Map GET /business/:business products into UI groups with variants. */
export function mapBusinessProductGroups(apiProducts) {
  return (apiProducts || []).map((group) => {
    const groupId = group._id ?? group.id
    const images = normalizeProductImages(group.images, group.imageUrl ?? group.image)
    const image = primaryProductImageUrl(images, group.imageUrl ?? group.image)
    const variants = (group.variants || []).map((v) => {
      const id = v._id ?? v.id
      return {
        ...v,
        id,
        _id: id,
        title: v.title ?? null,
        price: parsePrice(v.price),
        oldPrice: v.oldPrice != null ? parsePrice(v.oldPrice) : undefined,
        promotionId: v.promotionId ?? null,
      }
    })

    return {
      id: groupId,
      _id: groupId,
      name: group.name,
      description: group.description || '',
      images,
      image,
      imageUrl: image,
      variants,
      pinned: group.pinned === true,
    }
  })
}

/** Map GET /business/:business/categories rows. */
export function mapBusinessCategories(apiCategories) {
  return (apiCategories || []).map((category) => {
    const id = category._id ?? category.id
    return {
      id,
      _id: id,
      name: String(category.name ?? '').trim() || 'Категория',
      index: category.index ?? null,
    }
  }).filter((category) => category.id != null)
}

/** Flatten groups to sellable rows keyed by variant id (promotions, gifts, pricing maps). */
export function flattenBusinessProductGroups(groups) {
  const out = []
  for (const group of groups || []) {
    const variants = Array.isArray(group?.variants) ? group.variants : []
    const images = normalizeProductImages(group?.images, group?.image ?? group?.imageUrl)
    const image = primaryProductImageUrl(images, group?.image ?? group?.imageUrl)

    // Already flat / legacy
    if (variants.length === 0 && group?.price != null) {
      out.push(group)
      continue
    }

    for (const variant of variants) {
      const id = variant?.id ?? variant?._id
      if (id == null) continue
      out.push({
        id,
        _id: id,
        groupId: group.id ?? group._id,
        name: formatVariantDisplayName(group.name, variant.title),
        title: variant.title,
        price: variant.price,
        oldPrice: variant.oldPrice,
        promotionId: variant.promotionId,
        durationMinutes: variant.durationMinutes ?? null,
        images,
        image,
        imageUrl: image,
        description: group.description || '',
      })
    }
  }
  return out
}

/** Lowest current price among variants (for "от X" display). */
export function getGroupDisplayPrice(group) {
  const variants = group?.variants || []
  let min = NaN
  for (const v of variants) {
    const p = parsePrice(v?.price)
    if (!Number.isFinite(p)) continue
    if (!Number.isFinite(min) || p < min) min = p
  }
  return min
}
